import { useCallback, useState, useRef, useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { SelectOption } from '@kep-ui-kit/ui-components';

import {
  CardInputStatusType,
  cardInputStatuses,
  IIndexSignature,
  Chargebee,
  cardFormTypes,
  handleGtmErrors,
} from '@selfserve/react-utils';
import { ModalSettings } from '@selfserve/react-hocs';

import { useCountryList } from './use-country-list';
import { storage, storageKeys, useStorage } from './use-storage';

export interface FormValues {
  address?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  state?: string;
  firstName?: string;
  lastName?: string;
  vatNumber?: string;
  number?: CardInputStatusType;
  expiry?: CardInputStatusType;
  cvv?: CardInputStatusType;
}

interface IReturn {
  formik: FormikProps<FormValues>;
  apiError: string;
  isSubmited: boolean;
  handleDropdownChange: (name: string) => (value: string) => void;
  handleCardInputsChange: (fieldData: any) => void;
  handleCardInputsBlur: (fieldData: any) => void;
  cardRef: React.MutableRefObject<HTMLElement | undefined>;
  clearApiError: () => void;
  handleReset: () => void;
  handleApiError: (text: string) => void;
}

const validationSchema = (
  t: TFunction,
  states: IIndexSignature<SelectOption[]>,
  activeFormSection?: string
) => {
  const fullBillingFormData =
    activeFormSection === cardFormTypes.ADD_PAYMENT_METHOD ||
    !activeFormSection;
  const requiredBillingDetails =
    fullBillingFormData ||
    activeFormSection === cardFormTypes.EDIT_BILLING_INFO;
  const requiredPaymentDetails =
    fullBillingFormData ||
    activeFormSection === cardFormTypes.CHANGE_PAYMENT_METHOD;

  return Yup.object().shape({
    address: requiredBillingDetails
      ? Yup.string().required(t('common.validations.address'))
      : Yup.string(),
    city: requiredBillingDetails
      ? Yup.string().required(t('common.validations.city'))
      : Yup.string(),
    zipCode: requiredBillingDetails
      ? Yup.string().required(t('common.validations.zip-code'))
      : Yup.string(),
    country: requiredBillingDetails
      ? Yup.string().required(t('common.validations.country-required'))
      : Yup.string(),
    state: requiredBillingDetails
      ? Yup.string().when(
          'country',
          (country, schema) =>
            states[country] && schema.required(t('common.validations.state'))
        )
      : Yup.string(),
    number: requiredPaymentDetails
      ? Yup.string()
          .required(t(`common.validations.number`))
          .test('number', function (value) {
            if (value === cardInputStatuses.INVALID) {
              return this.createError({
                message: t(`common.validations.number-valid`),
              });
            }
            return true;
          })
      : Yup.string(),
    expiry: requiredPaymentDetails
      ? Yup.string()
          .required(t(`common.validations.expiry`))
          .test('expiry', function (value) {
            if (value === cardInputStatuses.INVALID) {
              return this.createError({
                message: t(`common.validations.expiry-valid`),
              });
            }
            return true;
          })
      : Yup.string(),
    cvv: requiredPaymentDetails
      ? Yup.string()
          .required(t(`common.validations.cvv`))
          .test('cvv', function (value) {
            if (value === cardInputStatuses.INVALID) {
              return this.createError({
                message: t(`common.validations.cvv-valid`),
              });
            }
            return true;
          })
      : Yup.string(),
    firstName: requiredPaymentDetails
      ? Yup.string().required(t('common.validations.firstname-required'))
      : Yup.string(),
    lastName: requiredPaymentDetails
      ? Yup.string().required(t('common.validations.lastname-required'))
      : Yup.string(),
    vatNumber: Yup.string(),
  });
};

export type OnSubmitType = (
  values: FormValues,
  isSubmited: boolean,
  setIsSubmited: (value: boolean) => void,
  cardComponents: HTMLElement | undefined,
  handleApiError: (text: string) => void,
  t: TFunction
) => void;

export type OpenErrorModalType = (
  text?: string | undefined,
  url?: string | undefined,
  withDissmiss?: boolean | undefined,
  dismissBtnText?: string | undefined,
  confirmBtnText?: string | undefined
) => void;

export interface CardFormikProps {
  onSubmitAction: OnSubmitType;
  openErrorModal: (settings?: ModalSettings) => void;
  activeFormSection?: string;
}

export const useCardFormik = ({
  onSubmitAction,
  openErrorModal,
  activeFormSection,
}: CardFormikProps): IReturn => {
  const { t } = useTranslation();
  const [countryCode, setCountryCode] = useStorage(
    storage.SESSION,
    storageKeys.COUNTRY_CODE
  );
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [apiError, setApiError] = useState('');
  const { states } = useCountryList();
  const cardRef = useRef<HTMLElement>();

  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange,
    initialValues: {
      number: cardInputStatuses.EMPTY,
      expiry: cardInputStatuses.EMPTY,
      cvv: cardInputStatuses.EMPTY,
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      zipCode: '',
      country: activeFormSection ? '' : countryCode || '',
      vatNumber: '',
      state: '',
    },
    validationSchema: validationSchema(t, states, activeFormSection),
    onSubmit: (values: FormValues) => {
      onSubmitAction(
        values,
        isSubmited,
        setIsSubmited,
        cardRef.current,
        handleApiError,
        t
      );
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
    setFieldError,
    validateForm,
    handleReset,
  } = formik;

  useEffect(() => {
    setApiError('');
  }, [activeFormSection]);

  useEffect(() => {
    validateForm();
  }, [
    values.address,
    values.city,
    values.country,
    values.cvv,
    values.expiry,
    values.firstName,
    values.lastName,
    values.number,
    values.state,
    values.vatNumber,
    values.zipCode,
    validateForm,
  ]);

  useEffect(() => {
    setFieldValue('state', '');
    setFieldValue('vatNumber', '');
    setCountryCode(values.country);
  }, [values.country, setFieldValue]);

  useEffect(() => {
    if (!Chargebee?.inited) {
      openErrorModal();
    }
  }, []);

  useEffect(() => {
    const hasError = !!Object.keys(errors).find(
      (key: string) => touched[key as keyof FormValues]
    );
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, errors, touched]);

  const handleDropdownChange = useCallback(
    (name: string) => (value: string) => {
      setFieldValue(name, value);
    },
    [setFieldValue]
  );

  const handleCardDataError = useCallback((fieldData: any) => {
    const { field, complete, error, empty } = fieldData;
    if (!complete || error || empty) {
      setFieldValue(field, cardInputStatuses[empty ? 'EMPTY' : 'INVALID']);
    } else {
      setFieldError(field, '');
      setFieldValue(field, cardInputStatuses.SUCCESS);
    }
    setFieldTouched(field, true);
  }, []);

  const handleCardInputsChange = useCallback((fieldData: any) => {
    setValidateOnChange((validateOnChange: boolean) => {
      if (validateOnChange) handleCardDataError(fieldData);
      return validateOnChange;
    });
  }, []);

  const handleCardInputsBlur = useCallback(
    (fieldData: any) => handleCardDataError(fieldData),
    []
  );

  const getErrorObject = useCallback((text: string) => {
    const errorArray = text.split(';');
    const errorText = errorArray[0];
    const errorCodeText = errorArray[1];
    let errorCode = errorCodeText ? errorCodeText.split(':')[1] : '';
    errorCode = errorCode.trim();
    return { errorCode, errorText };
  }, []);

  const handleApiError = useCallback(
    (text: string) => {
      setIsSubmited(false);
      if (!text) {
        return openErrorModal();
      }
      const errorObject = getErrorObject(text);
      if (errorObject.errorText?.includes('zip')) {
        setFieldError('zipCode', t('common.validations.zip-code-valid'));
      }
      const errorId = t(
        `credit-card-errors.${errorObject.errorCode || errorObject.errorText}`
      );
      if (errorId) {
        handleGtmErrors(+errorId);
        setApiError(errorObject.errorText);
      }
    },
    [setIsSubmited, openErrorModal, setFieldError, t]
  );

  const clearApiError = useCallback(() => {
    setApiError('');
  }, []);

  const handleResetForm = useCallback(() => {
    handleReset();
    setIsSubmited(false);
  }, [handleReset]);

  return {
    formik,
    handleDropdownChange,
    handleCardInputsChange,
    handleCardInputsBlur,
    apiError,
    isSubmited,
    cardRef,
    clearApiError,
    handleReset: handleResetForm,
    handleApiError,
  };
};
